/**
 * SubscribePage — Public hub for all TourismIntel RSS feeds + email subscribe CTA.
 * Route: /subscribe
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rss, Copy, Check, Mail, Headphones, Newspaper, FileText, Sparkles, Hotel } from "lucide-react";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";

const SITE = "https://tourismintel.ai";

const FEEDS = [
  {
    id: "main",
    icon: Rss,
    title: "All Travel Intelligence",
    subtitle: "Latest 20 curated articles across AI, hotel tech, innovation.",
    url: `${SITE}/api/rss`,
    badge: "Updated every 12h",
  },
  {
    id: "ai-travel",
    icon: Sparkles,
    title: "AI in Travel",
    subtitle: "Articles on artificial intelligence in travel and tourism.",
    url: `${SITE}/api/rss/ai-travel`,
    badge: "Filtered category",
  },
  {
    id: "hotel-tech",
    icon: Hotel,
    title: "Hotel & Hospitality Technology",
    subtitle: "PMS, revenue management, hotel tech and operations.",
    url: `${SITE}/api/rss/hotel-tech`,
    badge: "Filtered category",
  },
  {
    id: "briefs",
    icon: FileText,
    title: "Weekly Travel Intelligence Briefs",
    subtitle: "Editorial brief curated by Mirko Lalli. One per week.",
    url: `${SITE}/api/rss/briefs`,
    badge: "Weekly",
  },
  {
    id: "newsletters",
    icon: Newspaper,
    title: "Newsletter Archive",
    subtitle: "Every published newsletter, full content readable in feed.",
    url: `${SITE}/api/rss/newsletters`,
    badge: "On publish",
  },
  {
    id: "podcast",
    icon: Headphones,
    title: "TourismIntel Weekly — Podcast",
    subtitle: "Audio briefing. Apple Podcasts, Spotify, Pocket Casts compatible.",
    url: `${SITE}/api/rss/podcast`,
    badge: "Weekly audio",
  },
];

export default function SubscribePage() {
  const [copiedId, setCopiedId] = useState("");

  useEffect(() => {
    document.title = "Travel Industry Newsletter, RSS Feeds & Podcast — Subscribe Free | TourismIntel";
    const desc = document.querySelector('meta[name="description"]');
    const original = desc?.getAttribute("content");
    desc?.setAttribute(
      "content",
      "A free travel industry newsletter for tourism professionals — weekly intelligence brief plus 6 RSS feeds on AI in travel and hotel technology, and a weekly podcast. Compatible with Feedly, Inoreader, Apple Podcasts."
    );
    return () => { if (original) desc?.setAttribute("content", original); };
  }, []);

  const copyUrl = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1800);
    } catch {
      // Fallback for older browsers / restrictive contexts
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (_e) { /* ignore */ }
      document.body.removeChild(ta);
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1800);
    }
  };

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Space Grotesk', sans-serif" }}>
      <PublicPageNav active="subscribe" />

      {/* Hero */}
      <section style={{ background: "#fff", borderBottom: "1px solid #e8ecee", padding: "56px 24px 40px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Rss style={{ width: 16, height: 16, color: "#006874" }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b7a7d" }}>
              Subscribe
            </span>
          </div>
          <h1
            data-testid="subscribe-page-title"
            style={{ margin: 0, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#191c1d", lineHeight: 1.1 }}
          >
            The travel industry newsletter — and every other way to follow
          </h1>
          <p style={{ margin: "12px 0 0", fontSize: 16, color: "#6b7a7d", maxWidth: 620, lineHeight: 1.6 }}>
            A free weekly travel industry newsletter for tourism professionals, six dedicated RSS feeds —
            from breaking AI-in-travel articles to curated weekly briefs — and an audio podcast.
            Pick the format that fits your workflow.
          </p>

          {/* Email CTA */}
          <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <Link
              to="/"
              data-testid="subscribe-email-cta"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                fontSize: 14, fontWeight: 600, textDecoration: "none",
                padding: "10px 20px", borderRadius: 99,
                background: "#006874", color: "#fff",
              }}
            >
              <Mail style={{ width: 14, height: 14 }} />
              Subscribe by email (free)
            </Link>
            <span style={{ fontSize: 13, color: "#6b7a7d" }}>
              Or pick an RSS feed below — works with Feedly, Inoreader, Reeder.
            </span>
          </div>
        </div>
      </section>

      {/* Feed grid */}
      <section style={{ padding: "48px 24px 80px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {FEEDS.map((feed) => {
              const Icon = feed.icon;
              const copied = copiedId === feed.id;
              return (
                <article
                  key={feed.id}
                  data-testid={`feed-card-${feed.id}`}
                  style={{
                    background: "#fff",
                    border: "1px solid #e8ecee",
                    borderRadius: 16,
                    padding: 20,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div
                      style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: "#f0f9fa", color: "#006874",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <Icon style={{ width: 18, height: 18 }} />
                    </div>
                    <span
                      style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                        textTransform: "uppercase", color: "#6b7a7d",
                        background: "#f3f4f5", padding: "4px 8px", borderRadius: 6,
                      }}
                    >
                      {feed.badge}
                    </span>
                  </div>

                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#191c1d", letterSpacing: "-0.01em" }}>
                      {feed.title}
                    </h3>
                    <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b7a7d", lineHeight: 1.5 }}>
                      {feed.subtitle}
                    </p>
                  </div>

                  <div
                    style={{
                      marginTop: "auto",
                      display: "flex", alignItems: "center", gap: 8,
                      background: "#f8f9fa", borderRadius: 10, padding: "8px 10px",
                      border: "1px solid #e8ecee",
                    }}
                  >
                    <code
                      data-testid={`feed-url-${feed.id}`}
                      style={{
                        flex: 1, fontSize: 11, color: "#3b494c",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                    >
                      {feed.url}
                    </code>
                    <button
                      data-testid={`copy-btn-${feed.id}`}
                      onClick={() => copyUrl(feed.id, feed.url)}
                      aria-label={`Copy ${feed.title} RSS URL`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: 11, fontWeight: 600, cursor: "pointer",
                        padding: "5px 10px", borderRadius: 6, border: "none",
                        background: copied ? "#006874" : "#fff",
                        color: copied ? "#fff" : "#006874",
                        transition: "background 0.15s, color 0.15s",
                      }}
                    >
                      {copied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>

                  <a
                    href={feed.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`open-feed-${feed.id}`}
                    style={{
                      fontSize: 12, fontWeight: 600, color: "#006874",
                      textDecoration: "none",
                    }}
                  >
                    Open feed →
                  </a>
                </article>
              );
            })}
          </div>

          {/* How-to */}
          <div
            style={{
              marginTop: 48, padding: 24, borderRadius: 16,
              background: "#fff", border: "1px solid #e8ecee",
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#191c1d" }}>
              How to add a feed
            </h2>
            <ol style={{ margin: "12px 0 0", paddingLeft: 20, fontSize: 14, color: "#3b494c", lineHeight: 1.7 }}>
              <li>Copy the RSS URL above using the <strong>Copy</strong> button.</li>
              <li>Paste it into your reader: <a href="https://feedly.com/i/discover" target="_blank" rel="noopener noreferrer" style={{ color: "#006874" }}>Feedly</a>, <a href="https://www.inoreader.com/" target="_blank" rel="noopener noreferrer" style={{ color: "#006874" }}>Inoreader</a>, <a href="https://reederapp.com/" target="_blank" rel="noopener noreferrer" style={{ color: "#006874" }}>Reeder</a>, or any RSS reader.</li>
              <li>For the podcast, paste <code style={{ background: "#f3f4f5", padding: "2px 6px", borderRadius: 4 }}>{SITE}/api/rss/podcast</code> into Apple Podcasts, Spotify, or Overcast.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicPageFooter />
    </div>
  );
}
